import { AlertTriangle, Trash2, X } from 'lucide-react';
import { useDeletePackage } from '../hooks/useDeletePackage';

export const DeleteConfirmModal = (props) => {
  const { isDeleteModalOpen, setIsDeleteModalOpen, package: pkg } = props;
  const { mutate: deletePackage, isPending } = useDeletePackage();

  const handleDelete = () => {
    if (pkg?._id) {
      deletePackage(pkg._id, {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
        },
      });
    }
  };

  if (!isDeleteModalOpen || !pkg) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-md'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-border'>
          <div className='flex items-center space-x-2'>
            <div className='p-2 bg-destructive/10 rounded-lg'>
              <AlertTriangle className='text-destructive' size={20} />
            </div>
            <h2 className='text-lg font-semibold'>Delete Package</h2>
          </div>
          <button
            onClick={() => setIsDeleteModalOpen(false)}
            className='inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            disabled={isPending}
            aria-label='Close delete package modal'
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className='px-6 py-4 space-y-4'>
          <p className='text-sm text-muted-foreground'>
            Are you sure you want to delete the package{' '}
            <span className='font-semibold text-foreground'>"{pkg?.name}"</span>?
          </p>

          <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-3'>
            <p className='text-xs text-destructive'>
              This action cannot be undone. This will permanently delete the package and all its associated data. Any
              active bookings may be affected.
            </p>
          </div>

          {pkg && (
            <div className='bg-muted rounded-lg p-3 space-y-2'>
              <div className='text-xs text-muted-foreground'>Package Details:</div>
              <div className='space-y-1'>
                <div className='text-sm font-medium'>{pkg.name}</div>
                {pkg.slug && <div className='text-xs text-muted-foreground'>Slug: {pkg.slug}</div>}
                {pkg.serviceIds && (
                  <div className='text-xs text-muted-foreground'>Services: {pkg.serviceIds.length}</div>
                )}
                {pkg.pricing && (
                  <div className='text-xs text-muted-foreground'>
                    Price: {pkg.pricing.currency || 'INR'} {pkg.pricing.bookingPrice?.toFixed(2)}
                  </div>
                )}
                {pkg.stats?.totalBookings > 0 && (
                  <div className='text-xs font-medium text-orange-600 dark:text-orange-400'>
                    Warning: {pkg.stats.totalBookings} total bookings
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className='flex justify-end space-x-3 px-6 py-4 border-t border-border'>
          <button
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isPending}
            className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isPending}
            className='px-4 py-2 text-sm bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isPending ? (
              <div className='flex items-center justify-center'>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                Deleting...
              </div>
            ) : (
              <div className='flex items-center justify-center'>
                <Trash2 size={16} className='mr-2' />
                Delete Package
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
